import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Download,
  FileText,
  FileEdit,
  Ban,
  X,
  Clock,
  Hash,
  Loader2,
} from 'lucide-react';
import { api } from '../api/client';

interface Props {
  open: boolean;
  nfeId: number | null;
  nfeNumero: number | string | null;
  onClose: () => void;
}

interface EventoApi {
  id: number;
  tipo_evento: string;
  nome_evento: string;
  sequencial: number;
  justificativa: string | null;
  protocolo: string | null;
  c_stat_sefaz: string | null;
  x_motivo_sefaz: string | null;
  data_evento: string | null;
  data_resposta: string | null;
}

async function listarEventos(nfeId: number): Promise<EventoApi[]> {
  const { data } = await api.get<EventoApi[]>(`/nfe/${nfeId}/eventos`);
  return data;
}

function fmtData(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function evIcon(tpEvento: string) {
  if (tpEvento === '110110') return <FileEdit className="h-4 w-4 text-sky-700 dark:text-sky-400" />;
  if (tpEvento === '110111') return <Ban className="h-4 w-4 text-rose-700 dark:text-rose-400" />;
  return <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />;
}

function evLabel(ev: EventoApi): string {
  if (ev.tipo_evento === '110110') return 'Carta de Correção (CC-e)';
  if (ev.tipo_evento === '110111') return 'Cancelamento';
  return ev.nome_evento || `Evento ${ev.tipo_evento}`;
}

function badgeStatus(cstat: string | null): { cls: string; txt: string } {
  if (!cstat) return { cls: 'badge-neutral', txt: '-' };
  if (['135', '136', '155'].includes(cstat)) return { cls: 'badge-success', txt: `cStat ${cstat}` };
  return { cls: 'badge-warning', txt: `cStat ${cstat}` };
}

export default function EventosNfeModal({ open, nfeId, nfeNumero, onClose }: Props) {
  const q = useQuery({
    queryKey: ['nfe-eventos', nfeId],
    queryFn: () => listarEventos(nfeId!),
    enabled: open && !!nfeId,
  });

  if (!open || !nfeId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/70"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-ink-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-sky-700 dark:text-sky-400" />
            <h3 className="font-semibold text-slate-950 dark:text-white">
              Eventos SEFAZ, NF-e nº {nfeNumero ?? '-'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost !h-9 !w-9 !px-2 !py-1"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {q.isLoading && (
            <div className="grid place-items-center py-10 text-neutral-600 dark:text-neutral-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {q.isError && (
            <div className="py-6 text-center text-sm text-rose-700 dark:text-rose-300">
              Falha ao carregar eventos.
            </div>
          )}
          {!q.isLoading && !q.isError && (q.data?.length ?? 0) === 0 && (
            <div className="py-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
              Nenhum evento SEFAZ registrado para esta NF-e.
            </div>
          )}
          {!q.isLoading && (q.data?.length ?? 0) > 0 && (
            <ol className="space-y-3">
              {q.data!.map((ev) => {
                const b = badgeStatus(ev.c_stat_sefaz);
                return (
                  <li
                    key={ev.id}
                    className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{evIcon(ev.tipo_evento)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-950 dark:text-white">
                            {evLabel(ev)}
                          </span>
                          <span className="mono text-[10px] text-neutral-600 dark:text-neutral-400">
                            seq {ev.sequencial}
                          </span>
                          <span className={b.cls}>{b.txt}</span>
                          {ev.protocolo && (
                            <span className="mono flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400">
                              <Hash className="h-3 w-3" /> {ev.protocolo}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                          {fmtData(ev.data_evento)}
                          {ev.data_resposta ? `, resposta ${fmtData(ev.data_resposta)}` : ''}
                        </div>
                        {ev.x_motivo_sefaz && (
                          <div className="mt-1 text-xs text-slate-700 dark:text-neutral-300">
                            {ev.x_motivo_sefaz}
                          </div>
                        )}
                        {ev.justificativa && (
                          <div className="mt-1 border-l border-slate-200 pl-2 text-xs italic text-neutral-600 dark:border-white/10 dark:text-neutral-400">
                            {ev.justificativa}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ev.tipo_evento === '110110' && (
                            <>
                              <a className="btn-sky text-xs !py-1" href={`/api/nfe/${nfeId}/carta-correcao.pdf?seq=${ev.sequencial}`} target="_blank" rel="noreferrer">
                                <Download className="h-3 w-3" /> PDF
                              </a>
                              <a className="toolbar-btn" href={`/api/nfe/${nfeId}/carta-correcao.xml?seq=${ev.sequencial}`} target="_blank" rel="noreferrer">
                                <FileText className="h-3 w-3" /> XML
                              </a>
                            </>
                          )}
                          {ev.tipo_evento === '110111' && (
                            <>
                              <a className="btn-danger text-xs !py-1" href={`/api/nfe/${nfeId}/cancelamento.pdf`} target="_blank" rel="noreferrer">
                                <Download className="h-3 w-3" /> Comprovante PDF
                              </a>
                              <a className="toolbar-btn" href={`/api/nfe/${nfeId}/cancelamento.xml`} target="_blank" rel="noreferrer">
                                <FileText className="h-3 w-3" /> XML
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-white/10 dark:bg-slate-950/70">
          <button type="button" onClick={onClose} className="btn-secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
